import styled, { keyframes, css } from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";

// 방향별 슬라이드 인 애니메이션
const slideInRight = keyframes`
  0% { transform: translateX(100%); }
  100% { transform: translateX(0); }
`;

const slideInLeft = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(0); }
`;

const slideInTop = keyframes`
  0% { transform: translateY(-100%); }
  100% { transform: translateY(0); }
`;

const slideInBottom = keyframes`
  0% { transform: translateY(100%); }
  100% { transform: translateY(0); }
`;

// 방향별 슬라이드 아웃 애니메이션
const slideOutRight = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(100%); }
`;

const slideOutLeft = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-100%); }
`;

const slideOutTop = keyframes`
  0% { transform: translateY(0); }
  100% { transform: translateY(-100%); }
`;

const slideOutBottom = keyframes`
  0% { transform: translateY(0); }
  100% { transform: translateY(100%); }
`;

// 페이드 인/아웃 애니메이션
const fadeIn = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`;

const fadeOut = keyframes`
  0% { opacity: 1; }
  100% { opacity: 0; }
`;

export type SlideDirection = "right" | "left" | "top" | "bottom";

interface SlideWrapperProps {
  direction: SlideDirection;
  isExiting: boolean;
  width?: string;
  minWidth?: string;
  height?: string;
  padding?: string;
  $noBackdrop?: boolean;
}

// 방향에 따른 위치 및 크기 설정
const getPositionStyles = (direction: SlideDirection) => {
  switch (direction) {
    case "right":
      return css`
        top: 0;
        right: 0;
        width: ${(props) => props.width || "480px"};
        height: 100%;
      `;
    case "left":
      return css`
        top: 0;
        left: 0;
        width: ${(props) => props.width || "480px"};
        height: 100%;
      `;
    case "top":
      return css`
        top: 0;
        left: 0;
        width: 100%;
        height: ${(props) => props.height || "480px"};
      `;
    case "bottom":
      return css`
        bottom: 0;
        left: 0;
        width: 100%;
        height: ${(props) => props.height || "480px"};
      `;
    default:
      return css`
        top: 0;
        right: 0;
        width: ${(props) => props.width || "480px"};
        height: 100%;
      `;
  }
};

// 방향에 따른 애니메이션 설정
const getAnimationStyles = (direction: SlideDirection, isExiting: boolean) => {
  switch (direction) {
    case "right":
      return css`
        animation: ${isExiting ? slideOutRight : slideInRight} 0.5s forwards;
      `;
    case "left":
      return css`
        animation: ${isExiting ? slideOutLeft : slideInLeft} 0.5s forwards;
      `;
    case "top":
      return css`
        animation: ${isExiting ? slideOutTop : slideInTop} 0.5s forwards;
      `;
    case "bottom":
      return css`
        animation: ${isExiting ? slideOutBottom : slideInBottom} 0.5s forwards;
      `;
    default:
      return css`
        animation: ${isExiting ? slideOutRight : slideInRight} 0.5s forwards;
      `;
  }
};

export const SlidePopupWrapper = styled.div<{ $noBackdrop?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  pointer-events: ${({ $noBackdrop }) => ($noBackdrop ? "none" : "auto")};
  overscroll-behavior: contain;
  overflow: hidden;
`;

export const Backdrop = styled.div<{ isExiting: boolean; $noBackdrop?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  animation: ${({ isExiting }) => (isExiting ? fadeOut : fadeIn)} 0.5s forwards;
  transition: opacity 0.5s;
  display: ${({ $noBackdrop }) => ($noBackdrop ? "none" : "block")};
`;

export const SlideContainer = styled.div<SlideWrapperProps>`
  position: fixed;
  display: flex;
  flex-direction: column;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.15);
  z-index: 10000;
  padding: ${(props) => props.padding || "16px"};
  ${(props) => getPositionStyles(props.direction)}
  ${(props) => getAnimationStyles(props.direction, props.isExiting)}
  ${(props) => props.minWidth && `min-width: ${props.minWidth};`}
  transition: transform 0.5s;
  overflow: hidden;
  overscroll-behavior: contain;
  pointer-events: auto;
`;

export const CloseButtonWrapper = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 20; /* DrugLabelInfo 등 내부 sticky 헤더(z-index 10)보다 위 */
  background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const SlideContent = styled.div`
  flex: 1;
  width: 100%;
  overflow-y: auto;
`;
